import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  CircularProgress,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import { STUDENT_GAME_SUBJECTS } from '../../../config/studentGamesConfig';

const ALL_GAME_KEYS = [...new Set(STUDENT_GAME_SUBJECTS.flatMap((s) => s.games || []))].sort();

const GameTopicForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subjectId, setSubjectId] = useState('');
  const [topicId, setTopicId] = useState('');
  const [games, setGames] = useState([]);

  useEffect(() => {
    api.get('admin/game-topics/subjects')
      .then((r) => { if (r.data?.success && r.data?.data) setSubjects(r.data.data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!subjectId) {
      setTopics([]);
      setTopicId('');
      return;
    }
    api.get('admin/game-topics/topics', { params: { subject_id: subjectId } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setTopics(r.data.data);
        else setTopics([]);
      })
      .catch(() => setTopics([]));
    setTopicId('');
  }, [subjectId]);

  useEffect(() => {
    if (!isEdit) return;
    setFetching(true);
    api.get(`admin/game-topics/${id}`)
      .then((r) => {
        if (r.data?.success && r.data?.data) {
          const d = r.data.data;
          setSubjectId(d.subject_id ?? '');
          setTopicId(d.topic_id ?? '');
          setGames(Array.isArray(d.games) ? d.games : []);
        }
      })
      .catch(() => toast.error('Failed to load game topic'))
      .finally(() => setFetching(false));
  }, [id, isEdit]);

  useEffect(() => {
    if (!isEdit || !subjectId) return;
    api.get('admin/game-topics/topics', { params: { subject_id: subjectId } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setTopics(r.data.data);
        else setTopics([]);
      })
      .catch(() => setTopics([]));
  }, [isEdit, subjectId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subjectId || !topicId) {
      toast.error('Please select subject and topic');
      return;
    }
    if (!games.length) {
      toast.error('Please select at least one game');
      return;
    }
    setLoading(true);
    try {
      const payload = { subject_id: Number(subjectId), topic_id: Number(topicId), games };
      if (isEdit) {
        await api.put(`admin/game-topics/${id}`, payload);
        toast.success('Game topic updated');
      } else {
        await api.post('admin/game-topics', payload);
        toast.success('Game topic created');
      }
      navigate('/admin/game-topics');
    } catch (err) {
      const msg = err.response?.data?.message || (err.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(' ') : 'Failed to save');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/game-topics')}>
          Back
        </Button>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 0.5 }}>
          {isEdit ? 'Edit Game Topic' : 'Add Game Topic'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={subjectId}
                  onChange={(e) => setSubjectId(e.target.value)}
                  label="Subject"
                >
                  <MenuItem value="">Select subject</MenuItem>
                  {subjects.map((s) => (
                    <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required disabled={!subjectId}>
                <InputLabel>Topic</InputLabel>
                <Select
                  value={topicId}
                  onChange={(e) => setTopicId(e.target.value)}
                  label="Topic"
                >
                  <MenuItem value="">Select topic</MenuItem>
                  {topics.map((t) => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Games</InputLabel>
                <Select
                  multiple
                  value={games}
                  onChange={(e) => setGames(e.target.value)}
                  input={<OutlinedInput label="Games" />}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {ALL_GAME_KEYS.map((key) => (
                    <MenuItem key={key} value={key}>
                      <Checkbox checked={games.indexOf(key) > -1} />
                      <ListItemText primary={key} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Select the game keys that should appear under this topic in the student portal.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={loading} sx={{ bgcolor: '#5e35b1', '&:hover': { bgcolor: '#4527a0' } }}>
                {loading ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default GameTopicForm;
